// Sugalarm — header battery indicator (cycle 229).
//
// Tap-to-reveal readout (cycle 319, mirrors WifiIndicator): the glyph is
// always shown; tapping flashes the `NN%` label, which then fades away. It
// is NOT an alarm/ack control and is never paywalled — it only reveals a
// number. Renders an inline horizontal battery glyph (rounded body +
// terminal nub + an inner fill that tracks `level`), a charging bolt
// overlaid when `charging`. Colors are tokens only:
//   level > 20  → --zone-ok        (green)
//   level <= 20 → --zone-warn      (amber)
//   level <= 10 → --zone-urgent    (red)
// The glyph outline is --ink-2; the % label matches the level color.
//
// Renders nothing when level < 0 (unknown) — never a broken battery.
// Only mounted by KioskHeader when `battery.isAvailable()` (the Android
// tablet kiosk APK), so it never appears on the Pi kiosk, iPad, web, or
// mobile.

import { useEffect, useRef, useState } from "react";

import styles from "./BatteryIndicator.module.css";

interface Props {
  level: number;
  charging: boolean;
}

function levelColor(level: number): string {
  if (level <= 10) return "var(--zone-urgent)";
  if (level <= 20) return "var(--zone-warn)";
  return "var(--zone-ok)";
}

export function BatteryIndicator({ level, charging }: Props) {
  const [showPct, setShowPct] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  if (level < 0) return null;
  const clamped = Math.max(0, Math.min(100, Math.round(level)));
  const color = levelColor(clamped);
  // Inner fill spans up to 17 user-units inside the 20-wide body.
  const fillW = (17 * clamped) / 100;

  const reveal = (): void => {
    setShowPct(true);
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    // Flash the % then fade away (the CSS handles the opacity transition).
    timerRef.current = window.setTimeout(() => setShowPct(false), 2200);
  };

  return (
    <button
      type="button"
      className={styles.wrap}
      onClick={reveal}
      aria-label={`Battery ${clamped}%${charging ? ", charging" : ""}`}
      title={`Battery ${clamped}%${charging ? ", charging" : ""}`}
    >
      <svg
        className={styles.glyph}
        viewBox="0 0 26 20"
        fill="none"
        aria-hidden="true"
      >
        <rect
          x="1"
          y="4"
          width="20"
          height="12"
          rx="2.5"
          stroke="var(--ink-2)"
          strokeWidth="1.4"
        />
        <rect x="22.2" y="7" width="2.4" height="6" rx="1" fill="var(--ink-2)" />
        {clamped > 0 && (
          <rect
            x="2.6"
            y="5.6"
            width={fillW}
            height="8.8"
            rx="1.2"
            fill={color}
          />
        )}
        {charging && (
          <path
            d="M12.8 4.8 L9.2 11 H12 L11.2 15.2 L15.2 8.6 H12.4 Z"
            fill="var(--ink)"
            stroke="var(--bg-elev)"
            strokeWidth="0.7"
            strokeLinejoin="round"
          />
        )}
      </svg>
      <span
        className={`${styles.pct} tnum ${showPct ? styles.pctShown : ""}`}
        style={{ color }}
        aria-hidden="true"
      >
        {clamped}%
      </span>
    </button>
  );
}
